import {
  AutoAwesome,
  Delete,
  NavigateNextRounded,
  Settings,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TableCell } from "@/components/ui/table";

import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ActionsProps } from "./@types";

export function Actions<TData>({
  data,
  queryKey,
  ...props
}: Partial<ActionsProps<TData>>) {
  const queryClient = useQueryClient();

  const onUpdate = async () => {
    if (props.onUpdate && data) {
      await props.onUpdate!(data);
      queryClient.invalidateQueries({ queryKey });
    }
  };

  const onOpen = async () => {
    if (props.onOpen && data) {
      await props.onOpen!(data);
      queryClient.invalidateQueries({ queryKey });
    }
  };

  const onDelete = async () => {
    if (props.onDelete && data) {
      toast.promise(async () => await props.onDelete!(data), {
        loading: "A processar",
        error: (err) => (err as Error).message,
        success() {
          queryClient.invalidateQueries({ queryKey });
          return "Dados eliminados com sucesso";
        },
      });
    }
  };

  const onSetSpecialAction = async () => {
    if (props.onSetSpecialAction && data) {
      await props.onSetSpecialAction!(data);
      queryClient.invalidateQueries({ queryKey });
    }
  };

  if (!data || Object.keys(props).length <= 0)
    return <TableCell>---</TableCell>;

  return (
    <TableCell className="flex gap-4 w-fit">
      {props.onUpdate && (
        <IconButton
          color="inherit"
          size="small"
          onClick={onUpdate}
          disabled={!props.onUpdate}
        >
          <Settings color="inherit" />
        </IconButton>
      )}

      {props.onDelete && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <IconButton color="inherit" size="small" disabled={!props.onDelete}>
              <Delete color="inherit" />
            </IconButton>
          </AlertDialogTrigger>

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Tem a certeza absoluta?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. Isto irá eliminar
                permanentemente os dados selecionados dos servidores.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter className="gap-4">
              <AlertDialogCancel asChild>
                <Button variant="outline" className="bg-background">
                  Cancelar
                </Button>
              </AlertDialogCancel>

              <AlertDialogAction asChild>
                <Button
                  onClick={onDelete}
                  className="bg-primary text-background"
                >
                  Continuar
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {props.onSetSpecialAction && (
        <IconButton
          color="inherit"
          size="small"
          onClick={onSetSpecialAction}
          disabled={!props.onSetSpecialAction}
        >
          {props.specialActionIcon || <AutoAwesome color="inherit" />}
        </IconButton>
      )}

      {props.onOpen && (
        <IconButton
          color="inherit"
          size="small"
          onClick={onOpen}
          disabled={!props.onOpen}
        >
          <NavigateNextRounded color="inherit" />
        </IconButton>
      )}
    </TableCell>
  );
}
